import { SolaView, htmlSolaWithInstance } from '@hm-spt/sola';

import * as api from '../api/client';
import { MOVABLE_TABLES } from '../api/client';
import type { CatalogRowColumn, CatalogRowEvent, CatalogRowMoveInput, CatalogRowView, ECatalogTable, TableColumnView, TableColumnsView } from '../api/client';
import { assert } from '../lib/assert';
import { canEditCatalogRow } from '../lib/auth';
import { openReasonDialog } from './reason-dialog';
import type { ReasonChange } from './reason-dialog';
import { escapeHtml, fmtDateTime, renderDrawerValue } from '../lib/cells';
import { cellKindOf, cellTextOf, cellValueOf, columnHintOf, columnLabelOf } from '../pages/catalog-requests/form';

/** 카탈로그 확정 행 드로어의 상태 — 행(서버가 준 열 메타 · 잠김 · 이력) + 수정 모드의 입력 글 */
interface CatalogRowDrawerData {
  visible: boolean;
  table: ECatalogTable | null;
  view: CatalogRowView | null;               // null = 아직(또는 닫힘)
  editing: boolean;
  draft: Record<string, string>;             // 열린 열의 입력 글(키 = 열 이름). 수정 모드에서만
  saving: boolean;
  error: string;
  moveTable: ECatalogTable | null;           // 셀렉트로 고른 새 표(카테고리 옮기기). null = 안 옮김 — 지금 표
  moveColumns: TableColumnsView | null;      // 새 표의 열 목록(fetchTableColumns). 받는 동안 null
  moveDraft: Record<string, string>;         // 새 표 칸 입력 글(키 = 열 이름 · 모델 칸은 'model.<열>')
  moveCarried: Record<string, string>;       // 옛 표에만 있던 열 → 새 표의 열(셀렉트). '' = 메모 · specs 에 글로 보관(기본)
}

const BOOL_CHOICES: readonly (readonly [string, string])[] = [['true', '예'], ['false', '아니오'], ['', '비움']];
const EVENT_TYPE_WORD: Record<string, string> = { insert: '등록', update: '수정', pn_change: 'PN 교체', move: '표 옮김' };
const MODEL_KEY_PREFIX = 'model.';
const MOVE_TABLE_CHANGE = 'table';          // 옮기기 이벤트의 changes 키 — 표(카테고리)
const MOVE_ROW_ID_CHANGE = 'row_id';
// ② 갈 곳 셀렉트에서 뺄 새 표 열 — 열쇠 · 잠긴 열 · 메모 · 제원(값이 글로 섞여선 안 되는 자리)
const LOCKED_TARGET_NAMES: ReadonlySet<string> = new Set(['option_part', 'spare_part', 'assembly_part', 'factory_part', 'vendor', 'oem_vendor', 'description_raw', 'description', 'notes', 'specs']);        // 옮기기 이벤트의 changes 키 — 행 id(서버가 이력을 잇는 열쇠. 사람에겐 안 보인다)          // moveDraft 의 모델 칸 키 앞글 — 보낼 때 model_columns 로 가른다

/**
 * 카탈로그 확정 행 드로어 — 카탈로그 메뉴 · 요청함 전체 탭의 확정 줄이 연다(V장 "카탈로그 확정 행 수정", 2026-10-20).
 * 읽기: 열마다 한글 라벨(DB 주석) + 영어 열 이름 작게 + 값(잠긴 열은 자물쇠). [수정] → 열린 열이 입력(열 꼴대로) → [저장] 이 바뀐 열만 PUT. 맨 아래 변경 이력(catalog_event).
 */
export class CatalogRowDrawer extends SolaView<CatalogRowDrawerData> {
  /** 저장 · 옮기기 뒤 여는 쪽(카탈로그 표)이 목록을 다시 읽게 — 옮기면 그 줄의 id 가 바뀌어 옛 줄을 누르면 404(2026-10-20). 안 두면 아무것도 안 한다 */
  onRowChanged: (() => void) | null = null;

  override events = {
    click: { '@close': 'handleClose', '@overlay': 'handleClose', '@edit': 'handleEdit', '@cancel': 'handleCancel', '@save': 'handleSave' },
    input: { '@cell': 'handleCell', '@move-cell': 'handleMoveCell' },
    change: { '@cell': 'handleCell', '@move-cell': 'handleMoveCell', '@move-table': 'handleMoveTable', '@move-carry': 'handleMoveCarry' },
  };

  constructor() {
    super();
    this.data = { visible: false, table: null, view: null, editing: false, draft: {}, saving: false, error: '', moveTable: null, moveColumns: null, moveDraft: {}, moveCarried: {} };
  }

  /** 열고 서버에서 행을 받는다. 같은 드로어를 다른 행으로 다시 열면 수정 모드는 풀린다 */
  async open(table: ECatalogTable, id: number): Promise<void> {
    this.data = { ...this.data, visible: true, table, view: null, editing: false, draft: {}, saving: false, error: '', moveTable: null, moveColumns: null, moveDraft: {}, moveCarried: {} };
    this.rerender();
    try {
      const view = await api.fetchCatalogRowView(table, id);
      if (!this.data.visible || this.data.table !== table) return;   // 기다리는 사이 닫혔거나 다른 표로 갔으면 버린다
      this.data = { ...this.data, view };
    } catch (e) {
      this.data = { ...this.data, error: e instanceof Error ? e.message : String(e) };
    }
    this.rerender();
  }

  close(): void {
    this.data = { ...this.data, visible: false, editing: false, draft: {}, error: '', moveTable: null, moveColumns: null, moveDraft: {}, moveCarried: {} };
    this.rerender();
  }

  render(): HTMLElement {
    const { visible } = this.data;
    return htmlSolaWithInstance(this)`
      <aside class="drawer${visible ? '' : ' hidden'}" aria-hidden="${visible ? 'false' : 'true'}">
        <div class="drawer-overlay" data-action="overlay"></div>
        <div class="drawer-panel">${this.renderPanel()}</div>
      </aside>
    `;
  }

  // ─── 핸들러 ───

  handleClose(): void { this.close(); }

  /** [수정] — 열린 열의 지금 값을 입력 글로 */
  handleEdit(): void {
    const view = this.data.view;
    assert(view !== null, '[수정] 은 행을 받은 뒤에만 그려진다 — renderPanel');
    const draft = Object.fromEntries(view.columns.filter((column) => !column.locked).map((column) => [column.name, cellTextOf(column.value)]));
    this.data = { ...this.data, editing: true, draft, error: '' };
    this.rerender();
  }

  handleCancel(): void {
    this.data = { ...this.data, editing: false, draft: {}, error: '', moveTable: null, moveColumns: null, moveDraft: {}, moveCarried: {} };
    this.rerender();
  }

  /** 카테고리 셀렉트 — 지금 표면 옮기기를 접고, 다른 표면 그 표의 열 목록을 받아 새 표 칸을 편다. 고르기만으론 아무것도 안 바뀐다 — [저장] 이 옮긴다 */
  async handleMoveTable(_e: Event, el: HTMLElement): Promise<void> {
    const value = (el as HTMLSelectElement).value;
    const chosen = MOVABLE_TABLES.find((table) => table === value);
    assert(chosen !== undefined, `카테고리 셀렉트의 값은 MOVABLE_TABLES 중 하나 — renderMoveSection 이 그렸다: ${value}`);
    if (chosen === this.data.table) {
      this.data = { ...this.data, moveTable: null, moveColumns: null, moveDraft: {}, moveCarried: {}, error: '' };
      this.rerender();
      return;
    }
    this.data = { ...this.data, moveTable: chosen, moveColumns: null, moveDraft: {}, moveCarried: {}, error: '' };
    this.rerender();
    try {
      const moveColumns = await api.fetchTableColumns(chosen);
      if (this.data.moveTable !== chosen) return;   // 기다리는 사이 다른 표를 골랐으면 버린다
      this.data = { ...this.data, moveColumns };
    } catch (e) {
      this.data = { ...this.data, error: e instanceof Error ? e.message : String(e) };
    }
    this.rerender();
  }

  /** 옛 표에만 있던 값의 갈 곳 — moveCarried 만 바꾼다('' = 메모 · specs 에 글로) */
  handleMoveCarry(_e: Event, el: HTMLElement): void {
    const key = el.dataset.key;
    assert(key !== undefined, '갈 곳 셀렉트는 data-key(옛 열 이름)를 가진다 — renderMoveSection 이 붙였다');
    this.data = { ...this.data, moveCarried: { ...this.data.moveCarried, [key]: (el as HTMLSelectElement).value } };
  }

  /** 새 표 칸 입력 글 — moveDraft 만 바꾼다 */
  handleMoveCell(_e: Event, el: HTMLElement): void {
    const key = el.dataset.key;
    assert(key !== undefined, '새 표 칸은 data-key(열 이름 · model.<열>)를 가진다 — renderMoveSection 이 붙였다');
    this.data = { ...this.data, moveDraft: { ...this.data.moveDraft, [key]: (el as HTMLInputElement).value } };
  }

  /** 입력 글 — draft 만 바꾼다(다시 그리면 커서가 날아간다) */
  handleCell(_e: Event, el: HTMLElement): void {
    const key = el.dataset.key;
    assert(key !== undefined, '입력 칸은 data-key(열 이름)를 가진다 — renderCellInput 이 붙였다');
    this.data = { ...this.data, draft: { ...this.data.draft, [key]: (el as HTMLInputElement).value } };
  }

  /** [저장] — 바뀐 열만 PUT. 빈 글은 null. 실패면 글. 저장 뒤 서버가 준 행(이력 한 줄 늘어남)으로 바꿔 끼운다.
   *  바뀐 게 없으면 그냥 닫는다. 있으면 고친 이유 창(바뀐 칸 old → new + 이유 한 줄)을 띄우고, 취소하면 수정 모드 그대로(2026-10-27 B 안) */
  async handleSave(): Promise<void> {
    const { view, table, draft, moveTable } = this.data;
    assert(view !== null && table !== null, '[저장] 은 수정 모드에서만 그려진다 — renderPanel');
    const columns = changedColumnsOf(view, draft);
    if (moveTable === null && Object.keys(columns).length === 0) { this.handleCancel(); return; }
    const reasonNote = await openReasonDialog(reasonChangesOf(view, columns, table, moveTable));
    if (reasonNote === null) return;
    if (moveTable !== null) { await this.saveMove(view, table, moveTable, reasonNote); return; }
    this.data = { ...this.data, saving: true, error: '' };
    this.rerender();
    try {
      const saved = await api.updateCatalogRowView(table, view.id, columns, reasonNote);
      this.data = { ...this.data, view: saved, editing: false, draft: {}, saving: false };
      this.onRowChanged?.();
    } catch (e) {
      this.data = { ...this.data, saving: false, error: e instanceof Error ? e.message : String(e) };
    }
    this.rerender();
  }

  /** 카테고리 옮기기 — 고친 칸(draft)과 새 표 칸(moveDraft)을 한 입력으로 move. 받은 행은 새 표 · 새 id — 드로어가 그 행으로 바뀐다 */
  private async saveMove(view: CatalogRowView, table: ECatalogTable, moveTable: ECatalogTable, reasonNote: string): Promise<void> {
    const { moveColumns, draft, moveDraft, moveCarried } = this.data;
    if (moveColumns === null) { this.data = { ...this.data, error: '새 표의 칸을 아직 받는 중입니다' }; this.rerender(); return; }
    const input = moveInputOf(moveTable, moveColumns, { changed: changedColumnsOf(view, draft), moveDraft, moveCarried, reasonNote });
    this.data = { ...this.data, saving: true, error: '' };
    this.rerender();
    try {
      const moved = await api.moveCatalogRowView(table, view.id, input);
      const movedTable = MOVABLE_TABLES.find((candidate) => candidate === moved.table);
      assert(movedTable !== undefined, `옮긴 행의 표는 MOVABLE_TABLES 중 하나 — 서버가 input.table 로 넣었다: ${moved.table}`);
      this.data = { ...this.data, table: movedTable, view: moved, editing: false, draft: {}, saving: false, moveTable: null, moveColumns: null, moveDraft: {}, moveCarried: {} };
      this.onRowChanged?.();
    } catch (e) {
      this.data = { ...this.data, saving: false, error: e instanceof Error ? e.message : String(e) };
    }
    this.rerender();
  }

  // ─── 그리기 ───
  private renderPanel(): string {
    const { view, editing, saving, error } = this.data;
    const title = view === null ? '…' : (view.option_part === null ? `#${view.id}` : view.option_part);
    const subtitle = this.data.table === null ? '' : api.catalogTableWord(this.data.table);
    const buttons = view === null ? '' : editing
      ? `<button type="button" class="btn-ghost" data-action="cancel" ${saving ? 'disabled' : ''}>취소</button><button type="button" class="btn-primary" data-action="save" ${saving ? 'disabled' : ''}>${saving ? '저장 중…' : '저장'}</button>`
      : (canEditCatalogRow() ? `<button type="button" class="btn-ghost" data-action="edit">수정</button>` : '');   // 관리자만 — 백엔드도 403(2026-10-27)
    return `
      <header class="drawer-header">
        <div><h2 class="drawer-title mono">${escapeHtml(title)}</h2><span class="drawer-subtitle">${escapeHtml(subtitle)} · 확정 행${editing ? ' — 수정 중' : ''}</span></div>
        <div class="crd-buttons">${buttons}<button class="btn-icon" data-action="close" title="닫기">✕</button></div>
      </header>
      <div class="drawer-body">
        ${error === '' ? '' : `<div class="drawer-section text-danger">${escapeHtml(error)}</div>`}
        ${view === null ? (error === '' ? '<div class="drawer-section text-muted">불러오는 중…</div>' : '') : this.renderMoveSection(view) + this.renderColumns(view) + renderEvents(view.events)}
      </div>`;
  }

  private renderColumns(view: CatalogRowView): string {
    const rows = view.columns.map((column) => `
      <div class="kv-row${column.locked ? ' crd-locked' : ''}">
        <div class="kv-key">${escapeHtml(columnLabelOf(column))}${column.locked ? ' <span class="crd-lock" title="잠긴 열 — 파트넘버 · 원문 설명 · 제조사 · 모델은 여기서 안 고칩니다">🔒</span>' : ''} <span class="kv-key-raw">${escapeHtml(column.name)}</span></div>
        <div class="kv-val">${this.data.editing && !column.locked ? renderCellInput(column, this.data.draft[column.name] ?? '') : renderDrawerValue(column.value)}</div>
      </div>`);
    const title = this.data.editing && this.data.moveTable !== null ? '<h3 class="drawer-section-title">같이 저장되는 칸 <span class="crd-tag">옮기면서 고친 값도 새 행에 간다</span></h3>' : '';
    return `<div class="drawer-section">${title}${rows.join('')}</div>`;
  }

  /** 수정 모드 맨 위 — 카테고리 셀렉트(표 16). 다른 표를 고르면 묶음 셋 — ① 그대로 가는 칸 ② 옛 표에만 있던 값(갈 곳 셀렉트) ③ 새 표의 필수 칸(빈 것만) + 모델 절 */
  private renderMoveSection(view: CatalogRowView): string {
    const { editing, table, moveTable, moveColumns, moveDraft, moveCarried } = this.data;
    if (!editing || table === null) return '';
    const current = moveTable === null ? table : moveTable;
    const options = MOVABLE_TABLES.map((candidate) => `<option value="${candidate}" ${candidate === current ? 'selected' : ''}>${escapeHtml(api.catalogTableWord(candidate))}</option>`).join('');
    const select = `<div class="kv-row"><div class="kv-key">카테고리 <span class="kv-key-raw">table</span></div><div class="kv-val"><select class="input crd-cell" data-action="move-table">${options}</select></div></div>`;
    if (moveTable === null) return `<div class="drawer-section">${select}</div>`;
    if (moveColumns === null) return `<div class="drawer-section">${select}<div class="text-muted">${escapeHtml(api.catalogTableWord(moveTable))} 표의 칸을 받는 중…</div></div>`;
    const fromWord = escapeHtml(api.catalogTableWord(table));
    const toWord = escapeHtml(api.catalogTableWord(moveTable));
    const note = `<div class="crd-move-note"><span class="chip">${fromWord}</span><span class="crd-carry-arrow">→</span><span class="chip crd-move-to">${toWord}</span><span>아래 셋을 확인하고 [저장] 을 누르면 옮겨집니다. 값은 어느 칸도 사라지지 않습니다.</span></div>`;
    const kept = keptColumnsOf(view, moveColumns);
    const keptRows = kept.map((column) => { const label = moveLabelOf(column); return `<div class="kv-row"><div class="kv-key" title="${escapeHtml(label.title)}">${escapeHtml(label.label)} <span class="kv-key-raw">${escapeHtml(column.name)}</span></div><div class="kv-val">${renderDrawerValue(column.value)}</div></div>`; }).join('');
    const left = leftoverColumnsOf(view, moveColumns);
    const leftRows = left.map((column) => renderCarryRow(column, moveColumns, moveCarried[column.name] ?? '')).join('');
    const required = requiredMoveColumnsOf(view, moveColumns);
    const requiredRows = required.map((column) => renderMoveCell(column, column.name, moveDraft[column.name] ?? '')).join('');
    const modelRows = moveColumns.model === null ? '' : moveColumns.model.rows.map((column) => renderMoveCell(column, MODEL_KEY_PREFIX + column.name, moveDraft[MODEL_KEY_PREFIX + column.name] ?? '')).join('');
    const modelCard = moveColumns.model === null ? '' : renderMoveCard('', `모델 (${escapeHtml(moveColumns.model.table)})`, String(moveColumns.model.rows.length), '열쇠 칸으로 모델 행을 찾아 잇습니다 — 없으면 새로 만듭니다.', modelRows);
    return `<div class="drawer-section crd-move">${select}${note}
      ${renderMoveCard('1', '그대로 가는 칸', `이름이 같은 열 ${kept.length}`, '', keptRows === '' ? '<div class="text-muted">없음</div>' : keptRows)}
      ${renderMoveCard('2', `${fromWord} 에만 있던 값`, String(left.length), `${toWord} 엔 이 열이 없습니다. 어디에 남길지 고릅니다 — 기본은 메모(제원이 있으면 제원) 끝에 한 줄.`, left.length === 0 ? '<div class="text-muted">없음</div>' : leftRows)}
      ${renderMoveCard('3', `${toWord} 의 필수 칸`, String(required.length), '', required.length === 0 ? '<div class="text-muted">채울 필수 칸이 없습니다. 필수 아닌 빈 칸은 옮긴 뒤 드로어에서 고칩니다.</div>' : requiredRows)}
      ${modelCard}</div>`;
  }

  private rerender(): void {
    const root = this._rootElement;
    if (!root) return;
    root.classList.toggle('hidden', !this.data.visible);
    root.setAttribute('aria-hidden', this.data.visible ? 'false' : 'true');
    this.refreshRegion('.drawer-panel', () => this.renderPanel());
  }
}

// ─── 순수 ───
/** 입력 글 → 보낼 값(열 꼴대로). 지금 값과 같은 열은 뺀다 — 서버도 한 번 더 거른다. 빈 글은 null */
function changedColumnsOf(view: CatalogRowView, draft: Record<string, string>): Record<string, unknown> {
  const columns: Record<string, unknown> = {};
  for (const column of view.columns) {
    if (column.locked) continue;
    const text = draft[column.name];
    if (text === undefined) continue;
    const value = text.trim() === '' ? null : cellValueOf(text, column.data_type);
    if (JSON.stringify(value) !== JSON.stringify(column.value ?? null)) columns[column.name] = value;
  }
  return columns;
}

/** 고친 이유 창의 줄 — 옮기면 카테고리 한 줄을 먼저, 그 뒤 바뀐 칸(라벨 · 지금 값 → 새 값, 화면 글) */
function reasonChangesOf(view: CatalogRowView, columns: Record<string, unknown>, table: ECatalogTable, moveTable: ECatalogTable | null): ReasonChange[] {
  const moved: ReasonChange[] = moveTable === null ? [] : [{ label: '카테고리', old: api.catalogTableWord(table), new: api.catalogTableWord(moveTable) }];
  const changed: ReasonChange[] = view.columns
    .filter((column) => column.name in columns)
    .map((column) => ({ label: columnLabelOf(column), old: cellTextOf(column.value), new: cellTextOf(columns[column.name]) }));
  return [...moved, ...changed];
}

/** 지금 행에 값이 있고 새 표에도 같은 이름이 있는 열 — 그대로 간다(서버 movedRowOf). id · 시각은 열 목록에 없다 */
function keptColumnsOf(view: CatalogRowView, target: TableColumnsView): CatalogRowColumn[] {
  const targetNames = new Set(target.rows.map((column) => column.name));
  return view.columns.filter((column) => hasValue(column.value) && targetNames.has(column.name));
}

/** 지금 행에 값이 있는데 새 표엔 그 이름이 없는 열 — 사람이 갈 곳을 고른다(②) */
function leftoverColumnsOf(view: CatalogRowView, target: TableColumnsView): CatalogRowColumn[] {
  const targetNames = new Set(target.rows.map((column) => column.name));
  return view.columns.filter((column) => !column.locked && hasValue(column.value) && !targetNames.has(column.name));   // 잠긴 열(FK id · 파트넘버 · 제조사)은 ② 에 안 보인다 — cpu_model_id 1 을 메모에 글로 남길 뜻이 없다(QA 2026-10-20 #25)
}

/** 새 표의 필수 칸 중 지금 행에 같은 이름의 값이 없는 것 — 사람이 채운다(③). 필수 아닌 빈 칸은 안 보인다 */
function requiredMoveColumnsOf(view: CatalogRowView, target: TableColumnsView): TableColumnView[] {
  const filled = new Set(view.columns.filter((column) => hasValue(column.value)).map((column) => column.name));
  return target.rows.filter((column) => column.required && !filled.has(column.name));
}

function hasValue(value: unknown): boolean {
  return value !== null && value !== undefined && value !== '';
}

/** 옮기기 입력의 재료 — 고친 칸 · 새 표 칸 글 · 갈 곳 매핑 */
interface MoveInputParts { changed: Record<string, unknown>; moveDraft: Record<string, string>; moveCarried: Record<string, string>; reasonNote: string }

/** 고친 칸 + 새 표 칸 입력 글 + 매핑 → 보낼 입력. 값은 열 꼴대로(cellValueOf) · 'model.' 앞은 model_columns 로 · 빈 글은 뺀다 · 매핑은 '' 이 아닌 것만. 모델 표가 아니면 model_columns null */
function moveInputOf(table: ECatalogTable, target: TableColumnsView, parts: MoveInputParts): CatalogRowMoveInput {
  const columns: Record<string, unknown> = { ...parts.changed };
  const modelColumns: Record<string, unknown> = {};
  for (const column of target.rows) {
    const text = parts.moveDraft[column.name];
    if (text !== undefined && text.trim() !== '') columns[column.name] = cellValueOf(text, column.data_type);
  }
  if (target.model !== null) {
    for (const column of target.model.rows) {
      const text = parts.moveDraft[MODEL_KEY_PREFIX + column.name];
      if (text !== undefined && text.trim() !== '') modelColumns[column.name] = cellValueOf(text, column.data_type);
    }
  }
  const carried = Object.fromEntries(Object.entries(parts.moveCarried).filter(([, targetName]) => targetName !== ''));
  return { table, columns, model_columns: target.model === null ? null : modelColumns, carried, reason_note: parts.reasonNote };
}

/** ② 한 줄 — 옛 열의 값 → 갈 곳 셀렉트(메모 · specs 에 글로 보관 | 새 표의 열 하나) */
/** 옮기기 묶음 하나 — 번호 배지 · 제목 · 개수 칩 · 안내 한 줄 · 줄들. 번호 '' 이면 배지 없음(모델 절) */
function renderMoveCard(no: string, title: string, tag: string, lead: string, body: string): string {
  const badge = no === '' ? '' : `<span class="crd-step-no">${no}</span>`;
  return `<section class="crd-step"><h3 class="crd-step-head">${badge}<span>${title}</span><span class="crd-tag">${tag}</span></h3>${lead === '' ? '' : `<p class="crd-step-lead">${lead}</p>`}<div class="crd-step-body">${body}</div></section>`;
}

/** 옮기기 줄의 라벨 — 주석 첫 마디만(`:` 앞). 긴 주석(rail_type 의 값 설명)은 호버 title 로 */
function moveLabelOf(column: { name: string; comment: string | null }): { label: string; title: string } {
  const full = columnLabelOf(column);
  const cut = full.indexOf(':');
  return cut < 0 ? { label: full, title: '' } : { label: full.slice(0, cut).trim(), title: full };
}

function renderCarryRow(column: CatalogRowColumn, target: TableColumnsView, chosen: string): string {
  const options = target.rows
    .filter((candidate) => !LOCKED_TARGET_NAMES.has(candidate.name))
    .map((candidate) => `<option value="${escapeHtml(candidate.name)}" ${candidate.name === chosen ? 'selected' : ''}>${escapeHtml(columnLabelOf(candidate))} (${escapeHtml(candidate.name)})</option>`)
    .join('');
  const label = moveLabelOf(column);
  return `<div class="kv-row"><div class="kv-key" title="${escapeHtml(label.title)}">${escapeHtml(label.label)} <span class="kv-key-raw">${escapeHtml(column.name)}</span></div>
    <div class="kv-val crd-carry"><span class="crd-carry-value">${renderDrawerValue(column.value)}</span><span class="crd-carry-arrow">→</span>
    <select class="input crd-cell" data-action="move-carry" data-key="${escapeHtml(column.name)}"><option value="" ${chosen === '' ? 'selected' : ''}>메모 · 제원에 글로 보관</option>${options}</select></div></div>`;
}

/** 새 표 칸 한 줄 — 라벨(필수 *) + 입력(열 꼴대로). key 는 moveDraft 의 키 */
function renderMoveCell(column: TableColumnView, key: string, value: string): string {
  const input = renderCellInput({ ...column, value: null, locked: false }, value).replaceAll('data-action="cell"', 'data-action="move-cell"').replaceAll(`data-key="${escapeHtml(column.name)}"`, `data-key="${escapeHtml(key)}"`);
  const label = moveLabelOf(column);
  return `<div class="kv-row"><div class="kv-key" title="${escapeHtml(label.title)}">${escapeHtml(label.label)}${column.required ? ' <b class="crd-req">*</b>' : ''} <span class="kv-key-raw">${escapeHtml(column.name)}</span></div><div class="kv-val">${input}</div></div>`;
}

/** 값 입력 하나 — 요청함 폼과 같은 꼴(열의 꼴대로: 정수 · 소수 · 예/아니오 · JSON · 글). 긴 글(설명 · 메모)은 textarea */
function renderCellInput(column: CatalogRowColumn, value: string): string {
  const key = escapeHtml(column.name);
  const hint = escapeHtml(columnHintOf(column));
  switch (cellKindOf(column)) {
    case 'integer': return `<input type="number" step="1" class="input crd-cell" data-action="cell" data-key="${key}" value="${escapeHtml(value)}" placeholder="${hint}">`;
    case 'number':  return `<input type="number" step="any" class="input crd-cell" data-action="cell" data-key="${key}" value="${escapeHtml(value)}" placeholder="${hint}">`;
    case 'boolean': return `<span class="cr-bool">${BOOL_CHOICES.map(([choice, word]) => `<label><input type="radio" name="cell-${key}" data-action="cell" data-key="${key}" value="${choice}" ${value === choice ? 'checked' : ''}> ${word}</label>`).join('')}</span>`;
    case 'json':    return `<textarea class="input crd-cell crd-json" rows="3" data-action="cell" data-key="${key}" placeholder="${hint}">${escapeHtml(value)}</textarea>`;
    case 'text':    return column.name === 'description' || column.name === 'notes'
      ? `<textarea class="input crd-cell" rows="2" data-action="cell" data-key="${key}" placeholder="${hint}">${escapeHtml(value)}</textarea>`
      : `<input type="text" class="input crd-cell" data-action="cell" data-key="${key}" value="${escapeHtml(value)}" placeholder="${hint}">`;
  }
}

/** 바뀐 칸의 라벨 — 열 라벨(서버 주석) · 옮기기의 표는 '카테고리'. 옮기기 줄은 표 이름을 말로 */
function eventChangeLabelOf(change: { column: string; label: string }): string {
  return change.column === MOVE_TABLE_CHANGE ? '카테고리' : change.label;
}

/** 변경 이력 — 최신순. 줄 = 날짜 · 사람 · 종류 · 이유, 아래 바뀐 열(라벨 old → new) */
function renderEvents(events: readonly CatalogRowEvent[]): string {
  const rows = events.length === 0
    ? '<div class="text-muted">이력 없음</div>'
    : events.map((event) => `
      <div class="crd-event">
        <div class="crd-event-head"><span class="mono">${escapeHtml(fmtDateTime(event.at))}</span> · ${event.who === null ? '—' : escapeHtml(event.who)} · ${escapeHtml(EVENT_TYPE_WORD[event.type] ?? event.type)}${event.reason === null ? '' : ` · <span class="text-muted">${escapeHtml(event.reason)}</span>`}</div>
        ${event.changes.filter((change) => change.column !== MOVE_ROW_ID_CHANGE).map((change) => `<div class="crd-event-change"><b>${escapeHtml(eventChangeLabelOf(change))}</b> <span class="crd-old">${escapeHtml(cellTextOf(change.old) || '—')}</span> → <span class="crd-new">${escapeHtml(cellTextOf(change.new) || '—')}</span></div>`).join('')}
      </div>`).join('');
  return `<div class="drawer-section"><h3 class="drawer-section-title">변경 이력</h3>${rows}</div>`;
}
